/**
 * Workstations feature — URL state, the mint form and presentation helpers.
 *
 * The HTTP wire shapes are the Workstations operations in api/openapi/admin.yaml, generated into
 * api.generated.ts and re-exported below. The surface is the tenant admin's half of the Windows
 * credential provider's device plane: which machines are enrolled, which have never converged onto a
 * served policy, the enrolment tokens that let a machine join, and the policy those machines are
 * handed.
 *
 * TWO VOCABULARIES ARE DELIBERATELY OPEN (strings in the contract, not enums): `assurance` and
 * `status`. Both are expected to grow — assurance gains a value the moment machine attestation lands
 * — and an enum would turn that server-side addition into a parse failure that blanks the whole
 * roster. The label maps below fall back to the raw value for exactly that reason.
 *
 * WHAT THE API DOES NOT OFFER, recorded here rather than discovered by a reader looking for a
 * button:
 *   - NO DISABLE VERB. There is no endpoint for the reversible `disabled` state, so `status` moves
 *     only between active and revoked from this surface.
 *   - RE-REGISTER IS DEFERRED. A machine whose TPM was cleared is handled by minting a fresh token
 *     and enrolling again.
 *   - NO GROUP SCOPE. It is modelled in the schema and the resolver, and the API answers 501
 *     `scope_not_resolvable` for it because nothing records which machines belong to a group.
 *   - NO TRUNCATION FLAG ON THE ROSTER. The roster is capped at 500 with no `truncated` flag, unlike
 *     the impact endpoint which reports its own. `rosterMayBeTruncated` below is what this surface
 *     can honestly say instead.
 */

import { z } from "zod";
import {
  approveResultSchema,
  approvedWorkstationSchema,
  approveWorkstationsSchema,
  effectivePolicySchema,
  enrollmentTokenSchema,
  enrollmentTokensResponseSchema,
  mintedTokenSchema,
  policyDocumentSchema,
  policyImpactSchema,
  policyListResponseSchema,
  policyRowSchema,
  policyWarningSchema,
  policyWriteResultSchema,
  principalLabelSchema,
  revokedWorkstationSchema,
  seedExclusionSchema,
  seedFieldSchema,
  seedSchemaSchema,
  suppressionSchema,
  workstationEventSchema,
  workstationEventsResponseSchema,
  workstationSchema,
  workstationsResponseSchema,
} from "./api.generated";

// The HTTP wire shapes are generated from api/openapi/admin.yaml (the Workstations operations) and
// re-exported here; this file keeps the URL state, the mint form and the presentation helpers.
export {
  approveResultSchema,
  approvedWorkstationSchema,
  approveWorkstationsSchema,
  effectivePolicySchema,
  enrollmentTokenSchema,
  enrollmentTokensResponseSchema,
  mintedTokenSchema,
  policyDocumentSchema,
  policyImpactSchema,
  policyListResponseSchema,
  policyRowSchema,
  policyWarningSchema,
  policyWriteResultSchema,
  principalLabelSchema,
  revokedWorkstationSchema,
  seedExclusionSchema,
  seedFieldSchema,
  seedSchemaSchema,
  suppressionSchema,
  workstationEventSchema,
  workstationEventsResponseSchema,
  workstationSchema,
  workstationsResponseSchema,
};

// ── List state, which lives in the URL ─────────────────────────────────────

/**
 * Roster filter.
 *
 * `never_converged` is first among equals and is the reason this filter exists at all: a machine
 * that has never fetched a policy document is running on whatever its installer seeded, and from
 * every other surface in the product it looks exactly like a working machine.
 */
export const workstationFilterSchema = z.enum([
  "all",
  "never_converged",
  "self_enrolled",
  "software_key",
  "not_active",
]);
export type WorkstationFilter = z.infer<typeof workstationFilterSchema>;

export const workstationsSearchSchema = z.object({
  /**
   * Narrow the roster to the machines ONE person's sign-ins authorised. A user id, not a name: the
   * value comes from a roster cell and goes back to the server as-is, and it must survive a person
   * being renamed or deprovisioned — which is exactly when this question gets asked.
   */
  authorised_by: z.string().optional(),
  /** Roster filter. Optional so a bare /workstations URL is canonical. */
  status: workstationFilterSchema.optional(),
  /**
   * The machine whose effective policy is open. A drill-in that is a shareable link — "this is the
   * machine that is not taking the lockdown, and here is why" is a sentence an operator needs to be
   * able to paste into a ticket.
   */
  device: z.string().optional(),
  /**
   * A correlation GUID, filtering the reported trail. It is the query that feature exists for — an
   * operator arrives holding this value out of a machine's own log.
   */
  correlation: z.string().optional(),
});
export type WorkstationsSearch = z.infer<typeof workstationsSearchSchema>;

// ── The roster ────────────────────────────────────────────────────────────

export type Workstation = z.infer<typeof workstationSchema>;

export type PrincipalLabel = z.infer<typeof principalLabelSchema>;

export type WorkstationsResponse = z.infer<typeof workstationsResponseSchema>;

/** The status a revoked machine holds. Terminal — nothing moves it back. */
export const STATUS_REVOKED = "revoked";

export type RevokedWorkstation = z.infer<typeof revokedWorkstationSchema>;

/**
 * Admitting a machine that enrolled itself.
 *
 * WHY THE OUTCOME IS A STRING AND NOT A BOOLEAN. A bulk approval over a roster selection
 * legitimately mixes results: some machines were pending, some an admin already approved, some were
 * never pending (a token machine was admitted at mint time), some are cut off, and some ids are
 * stale. Every one of those is a 200 — collapsing them to succeeded/failed would make a partially
 * stale selection look like a failed request and invite a retry of something that already worked.
 *
 * OPEN VOCABULARY, matching this file's assurance handling exactly: an unrecognised outcome renders
 * as itself rather than blanking the dialog, so a server that grows a sixth outcome degrades to a
 * readable word instead of a parse failure that loses the other 499 results.
 */
export const APPROVAL_OUTCOME_LABEL: Record<string, string> = {
  approved: "Approved",
  already_approved: "Already approved",
  not_pending: "Nothing to approve",
  not_active: "Cut off",
  unknown: "No longer in the roster",
};

export function approvalOutcomeLabel(outcome: string): string {
  return APPROVAL_OUTCOME_LABEL[outcome] ?? outcome;
}

export type ApproveResult = z.infer<typeof approveResultSchema>;

/** The single-machine response, which additionally reports the assurance the row now carries. */
export type ApprovedWorkstation = z.infer<typeof approvedWorkstationSchema>;

export type ApproveWorkstationsResult = z.infer<typeof approveWorkstationsSchema>;

/** The server refuses more than this in one call; the console must not offer a larger selection. */
export const APPROVAL_BATCH_LIMIT = 500;

/**
 * The server's roster query is `ORDER BY enrolled_at DESC LIMIT 500` and returns no truncation
 * flag — unlike the impact endpoint, which reports its own. So the honest thing this surface can
 * say is "the answer may be of the 500 most recently enrolled", and it says it only when the count
 * is exactly at the ceiling. A fleet of exactly 500 machines gets one over-cautious sentence; a
 * fleet of 900 does not get a silently wrong convergence count.
 */
export const ROSTER_LIMIT = 500;
export function rosterMayBeTruncated(rows: readonly Workstation[]): boolean {
  return rows.length >= ROSTER_LIMIT;
}

/**
 * A machine is waiting for an admin exactly when it enrolled itself and is still live.
 *
 * DERIVED HERE RATHER THAN ASKED OF THE SERVER. The roster already carries both fields, and a
 * second source for "is this pending?" is a second thing that can disagree with the resolver — which
 * tests the same `assurance` value to decide whether to shield the machine. One predicate, read by
 * the queue and by the row badge alike.
 *
 * A REVOKED SELF-ENROLLED MACHINE IS NOT PENDING, and that is not a detail: approving it would
 * promote a machine an admin deliberately cut off, and the server refuses it as `not_active`. The
 * console must not offer the action it knows will be refused.
 */
export function isPendingApproval(w: Workstation): boolean {
  return w.assurance === ASSURANCE_SELF_ENROLLED && w.status === "active";
}

/**
 * Which authorisation enrolled a machine. Sentence case, no internal jargon: the design calls
 * these "rungs" and the console never does.
 *
 * `self_enrolled` is the one an operator must be able to pick out at a glance, because it is the
 * assurance the two post-resolution shields key on — such a machine can never receive
 * `exclusiveMode: true` or `fingerprintOnly`, whatever the tenant policy says.
 */
export const ASSURANCE_LABEL: Record<string, string> = {
  token: "Enrolment token",
  token_batch: "Imaging batch",
  self_enrolled: "User sign-in",
  attested: "Machine attestation",
  admin_approved: "Admin approved",
};

export const ASSURANCE_NOTE: Record<string, string> = {
  token: "An admin minted a single-use token bound to this machine's key.",
  token_batch:
    "Enrolled from a shared imaging token. Whoever held the image could have enrolled it.",
  self_enrolled:
    "One user's sign-in on this machine authorised the enrolment. Lockdown settings are withheld until an admin approves it.",
  attested: "The machine's own hardware attested to its identity.",
  admin_approved:
    "An admin admitted this machine. Settings that can lock a user out of it now apply to it.",
};

/** Falls back to the raw value: an unrecognised assurance must render as itself, never as blank. */
export function assuranceLabel(assurance: string): string {
  return ASSURANCE_LABEL[assurance] ?? assurance;
}

/** The value the two post-resolution shields test against. */
export const ASSURANCE_SELF_ENROLLED = "self_enrolled";

export const KEY_PROTECTION_LABEL: Record<string, string> = {
  tpm: "TPM",
  software: "Software",
};

export function keyProtectionLabel(protection: string): string {
  return KEY_PROTECTION_LABEL[protection] ?? protection;
}

/** The name to show for a machine. The thumbprint when it never told us one. */
export function workstationLabel(machine: Workstation): string {
  return machine.hostname_reported ?? machine.jkt;
}

/** Has this machine ever received a signed policy document? */
export function hasConverged(machine: Workstation): boolean {
  return machine.last_config_fetch_at !== undefined;
}

/**
 * Is this machine one that person's sign-in authorised?
 *
 * A SEPARATE PREDICATE FROM THE SEGMENTED FILTER, deliberately: the two compose. "Every machine this
 * person enrolled, of which the ones running on their installer seed" is the incident question, and
 * folding this into the single-choice filter vocabulary would make it unaskable.
 */
export function matchesAuthorisedBy(machine: Workstation, userID: string | undefined): boolean {
  if (!userID) return true;
  return machine.enrolled_by_user_id === userID;
}

export function matchesFilter(machine: Workstation, filter: WorkstationFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "never_converged":
      return !hasConverged(machine);
    case "self_enrolled":
      return machine.assurance === ASSURANCE_SELF_ENROLLED;
    case "software_key":
      return machine.key_protection_reported === "software";
    case "not_active":
      return machine.status !== "active";
  }
}

/**
 * The counts the convergence panel leads with, derived in exactly one place.
 *
 * `neverConverged` counts ACTIVE machines only. A revoked machine that never converged is not an
 * operational problem to chase — it is a row that will never converge by design — and counting it
 * beside the machines that should be fetching policy and are not would inflate the one number this
 * page exists to make true.
 */
export interface FleetCounts {
  total: number;
  active: number;
  neverConverged: number;
  selfEnrolled: number;
  softwareKey: number;
}

export function fleetCounts(rows: readonly Workstation[]): FleetCounts {
  let active = 0;
  let neverConverged = 0;
  let selfEnrolled = 0;
  let softwareKey = 0;
  for (const machine of rows) {
    const isActive = machine.status === "active";
    if (isActive) active += 1;
    if (isActive && !hasConverged(machine)) neverConverged += 1;
    if (machine.assurance === ASSURANCE_SELF_ENROLLED) selfEnrolled += 1;
    if (machine.key_protection_reported === "software") softwareKey += 1;
  }
  return { total: rows.length, active, neverConverged, selfEnrolled, softwareKey };
}

// ── Enrolment tokens ──────────────────────────────────────────────────────

export type EnrollmentToken = z.infer<typeof enrollmentTokenSchema>;

export type EnrollmentTokensResponse = z.infer<typeof enrollmentTokensResponseSchema>;

export type MintedToken = z.infer<typeof mintedTokenSchema>;

/** Is this token still redeemable? Only `pending` is; the other three are terminal. */
export function isTokenPending(token: EnrollmentToken): boolean {
  return token.status === "pending";
}

// ── The mint form ─────────────────────────────────────────────────────────

/**
 * An RFC 7638 thumbprint: 43 characters of unpadded base64url. Mirrors the server's
 * `isRFC7638Thumbprint`, which is deliberate rather than duplicative — the server refuses a
 * mistyped value either way, and catching it in the form is the difference between an admin fixing
 * a paste and an admin reading a 400.
 *
 * The commonest wrong paste is a 64-character hex SPKI hash, which is what the GATEWAY surface
 * asks for. It binds nothing here and presents later as a machine that simply cannot enrol, so the
 * message names it.
 */
const JKT = /^[A-Za-z0-9_-]{43}$/;

/**
 * Lifetime as a closed set rather than a number, so the 7-day ceiling the server enforces cannot be
 * typed past in the form, and so the field needs no coercion (coercion widens the input type and
 * breaks react-hook-form's resolver generic).
 */
export const tokenLifetimeSchema = z.enum(["1h", "24h", "7d"]);
export type TokenLifetime = z.infer<typeof tokenLifetimeSchema>;

export const TOKEN_LIFETIME_SECONDS: Record<TokenLifetime, number> = {
  "1h": 60 * 60,
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
};

/** The server's ceiling on a batch token's device cap. Mirrors MaxBatchDeviceCap. */
export const MAX_BATCH_DEVICE_CAP = 1000;

/**
 * How this token may be redeemed, as a DISCRIMINATED UNION rather than a checkbox beside two
 * optional fields — because the two branches are different acts and the server refuses any mix of
 * them (a thumbprint and `batch` cannot both be stated; a batch needs a cap of at least 2; a bound
 * token may not carry a cap above 1). Making it the discriminant means an unbound mint cannot
 * smuggle a thumbprint and a bound one cannot omit it, in the type as well as at runtime.
 *
 * No `.default()` anywhere in here: Zod 4 widens the input type to undefined-allowed and breaks
 * react-hook-form's SubmitHandler generic. Defaults live in the form's defaultValues.
 */
export const mintTokenFormSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("bound"),
    jkt: z
      .string()
      .regex(
        JKT,
        "43 characters of unpadded base64url. The machine prints it after it generates its device key — a 64-character hex fingerprint is the Edge deployments value and binds nothing here.",
      ),
    lifetime: tokenLifetimeSchema,
  }),
  z.object({
    mode: z.literal("batch"),
    device_cap: z
      .number({ message: "Enter how many machines this image will enrol." })
      .int("Whole machines only.")
      .min(2, "A batch needs at least 2. For a single machine, bind the token to its key instead.")
      .max(MAX_BATCH_DEVICE_CAP, `The ceiling is ${MAX_BATCH_DEVICE_CAP}.`),
    lifetime: tokenLifetimeSchema,
  }),
]);
export type MintTokenForm = z.infer<typeof mintTokenFormSchema>;

// ── Policy: the stored overrides ──────────────────────────────────────────

/**
 * The three modelled scopes. `group` is real in the schema and in the resolver and is REFUSED by
 * the write path with 501 `scope_not_resolvable` — nothing records which machines belong to a
 * group, so a row would apply to nobody while appearing to apply to some. This surface therefore
 * offers tenant and device only, and never renders a group control; it can still READ a group value
 * in provenance, which is why the layer labels below carry one.
 */
export const POLICY_SCOPE_TENANT = "tenant";
export const POLICY_SCOPE_DEVICE = "device";

export type PolicyRow = z.infer<typeof policyRowSchema>;

/**
 * `ListPolicies` is `ORDER BY (tenant, group, device), updated_at DESC LIMIT 500`, and returns no
 * truncation flag.
 *
 * The ordering saves the tenant row — scope 'tenant' sorts first, so it is always in the answer.
 * DEVICE rows are what fall out, oldest-edited first, and that is dangerous in a specific way:
 * a machine whose override is outside the window reads as having none, while the effective endpoint
 * (which resolves the real stored patch) goes on reporting its fields as coming from "this machine".
 * A whole-patch save from a form seeded with the empty patch would then clear an override the form
 * never showed. So this is not a display caveat — it is the gate on device-scope editing.
 */
export const POLICY_LIST_LIMIT = 500;
export function policyListMayBeTruncated(rows: readonly PolicyRow[]): boolean {
  return rows.length >= POLICY_LIST_LIMIT;
}

// ── The installer seed's published vocabulary ─────────────────────────────

export type SeedField = z.infer<typeof seedFieldSchema>;

export type SeedExclusion = z.infer<typeof seedExclusionSchema>;

export type SeedSchema = z.infer<typeof seedSchemaSchema>;

/**
 * How a seed field's wire type reads on this surface. Open, falling back to the raw kind.
 *
 * "Text" rather than "string": the reader is an admin writing a JSON file, and the JSON type is
 * visible in the file they are writing. What they need from this column is which shape of value the
 * field takes.
 */
export const SEED_KIND_LABEL: Record<string, string> = {
  bool: "True or false",
  string: "Text",
  uint32: "Whole number",
  "string[]": "List of text",
};

export function seedKindLabel(kind: string): string {
  return SEED_KIND_LABEL[kind] ?? kind;
}

/**
 * The exclusions, split the way the two groups differ.
 *
 * Refused first: those are the ones an installer author is most likely to have already tried, and
 * the ones whose absence would otherwise look like an oversight rather than a decision.
 */
export function splitSeedExclusions(exclusions: readonly SeedExclusion[]): {
  refused: SeedExclusion[];
  notCarried: SeedExclusion[];
} {
  const refused: SeedExclusion[] = [];
  const notCarried: SeedExclusion[] = [];
  for (const exclusion of exclusions) {
    (exclusion.refused ? refused : notCarried).push(exclusion);
  }
  return { refused, notCarried };
}

export type PolicyListResponse = z.infer<typeof policyListResponseSchema>;


// ── Policy: the resolved document ─────────────────────────────────────────

export type PolicyDocument = z.infer<typeof policyDocumentSchema>;

/**
 * Read one dotted path out of a resolved document.
 *
 * The document is walked as plain data rather than switched on per path — the alternative is a second
 * hardcoded copy of the document shape, which is the thing the served vocabulary exists to avoid.
 *
 * BUT THE SCHEMA ABOVE IS STILL A GATE, and the walk cannot see past it: Zod strips keys the schema
 * does not name, so a newly-served path reaches this function as `undefined` and formats as "Not set"
 * however faithfully the walk is written. Adding a settable path to the server means adding it to
 * `policyDocumentSchema` in the same change.
 */
export function policyValueAtPath(doc: PolicyDocument, path: string): unknown {
  let cursor: unknown = doc;
  for (const segment of path.split(".")) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/** Where a resolved field's value came from. Open string for the reason given on ASSURANCE_LABEL. */
export const POLICY_LAYER_LABEL: Record<string, string> = {
  builtin: "Built-in default",
  tenant: "Tenant default",
  group: "Group",
  device: "This machine",
};

export function policyLayerLabel(layer: string): string {
  return POLICY_LAYER_LABEL[layer] ?? layer;
}

export type Suppression = z.infer<typeof suppressionSchema>;

export const SUPPRESSION_REASON_LABEL: Record<string, string> = {
  self_enrolled: "Enrolled by a user sign-in",
};

export const SUPPRESSION_REASON_DETAIL: Record<string, string> = {
  // NAMES THE REMEDY THAT SCALES. This used to end "enrol it with an admin token instead", which is
  // only actionable one machine at a time — and Rung 0 is the zero-touch path, so it is how a fleet
  // enrols. That wording turned a bound meant to say "an admin has not admitted this machine yet"
  // into "this setting is unreachable for your whole estate". Approving the machine is the
  // documented promotion (implementation Rung 3) and it works over a selection.
  self_enrolled:
    "This machine was enrolled on the authority of one person's sign-in, so it is not yet handed a setting that can lock a user out of it. Approve the machine to admit it — you can approve a whole selection at once — and this setting takes effect on its next policy fetch.",
};

export type PolicyWarning = z.infer<typeof policyWarningSchema>;

export type EffectivePolicy = z.infer<typeof effectivePolicySchema>;

// ── Policy: the fleet view ────────────────────────────────────────────────

export type PolicyImpact = z.infer<typeof policyImpactSchema>;

// ── Policy: what a write did ──────────────────────────────────────────────

export type PolicyWriteResult = z.infer<typeof policyWriteResultSchema>;

/**
 * The enumerated refusal reasons a patch write can carry, alongside the offending path. Programmed
 * against by code rather than by string-matching the sentence, which is why the server enumerates
 * them at all; the sentence travels beside the code and is shown too.
 */
export const PATCH_REASON_LABEL: Record<string, string> = {
  unknown_field: "That is not a setting this server knows.",
  wrong_type: "That value is the wrong type for this setting.",
  out_of_vocabulary: "That value is outside the set this setting allows.",
  out_of_range: "That value is outside the range the Windows client accepts.",
  too_many_fields: "The change names more settings than the document has.",
  not_an_object: "That setting is a group of settings, not a setting.",
};

/** A patch refusal, as it arrives in the 400 body. Parsed leniently — the status is the signal. */
export const patchRefusalSchema = z.object({
  error: z.string(),
  path: z.string().optional(),
  reason: z.string().optional(),
  detail: z.string().optional(),
});
export type PatchRefusal = z.infer<typeof patchRefusalSchema>;

/**
 * Read a patch refusal out of an unknown error body. Returns null when the body is not one, so a
 * caller falls back to its own copy rather than rendering a half-parsed refusal.
 */
export function patchRefusalOf(body: unknown): PatchRefusal | null {
  const parsed = patchRefusalSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}

// ── The trail the machines upload ─────────────────────────────────────────

export type WorkstationEvent = z.infer<typeof workstationEventSchema>;

export type WorkstationEventsResponse = z.infer<typeof workstationEventsResponseSchema>;

/**
 * The trail's own page ceiling. `ListAuditEvents` defaults to 200 and reports no truncation flag,
 * so — like the roster — the honest thing a console can say is that the answer may be the newest
 * 200, and only when the count sits exactly on it.
 */
export const EVENT_LIMIT = 200;
export function eventsMayBeTruncated(rows: readonly WorkstationEvent[]): boolean {
  return rows.length >= EVENT_LIMIT;
}

/** A correlation GUID, validated before it is put in the URL — the server refuses anything else. */
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export function isCorrelationID(value: string): boolean {
  return UUID.test(value);
}
